/**
 * src/shell/services/NativeAdapter.ts
 *
 * Isomorphic Native Bridge.
 * Unifies Electron IPC, Mobile Capacitor bridges, and WebCrypto fallback.
 * Strictly abstracts platform-specific calls so UI code never imports direct native APIs.
 */

export interface INativeKeychain {
  getSecret(key: string): Promise<Uint8Array | null>;
  setSecret(key: string, value: Uint8Array): Promise<void>;
  deleteSecret(key: string): Promise<void>;
}

export interface INativeBiometrics {
  isAvailable(): Promise<boolean>;
  authenticate(reason: string): Promise<boolean>;
}

export interface INativeHardware {
  getDeviceId(): Promise<string>;
  bleTransmit(payload: Uint8Array): Promise<Uint8Array>;
}

export interface INativePlatform {
  platform: 'electron' | 'capacitor' | 'web';
  keychain: INativeKeychain;
  biometrics: INativeBiometrics;
  hardware: INativeHardware;
}

/**
 * In-memory fallback for pure Web runtime with WebCrypto
 */
class FallbackWebKeychain implements INativeKeychain {
  private store: Map<string, Uint8Array> = new Map();

  async getSecret(key: string): Promise<Uint8Array | null> {
    const val = this.store.get(key);
    return val ? new Uint8Array(val) : null;
  }

  async setSecret(key: string, value: Uint8Array): Promise<void> {
    this.store.set(key, new Uint8Array(value));
  }

  async deleteSecret(key: string): Promise<void> {
    const existing = this.store.get(key);
    if (existing) {
      existing.fill(0); // Zero-out on delete
      this.store.delete(key);
    }
  }
}

class FallbackWebBiometrics implements INativeBiometrics {
  async isAvailable(): Promise<boolean> {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
  }

  async authenticate(_reason: string): Promise<boolean> {
    return true;
  }
}

class FallbackWebHardware implements INativeHardware {
  async getDeviceId(): Promise<string> {
    return 'web_device_' + Math.random().toString(36).substring(2, 10);
  }

  async bleTransmit(payload: Uint8Array): Promise<Uint8Array> {
    return new Uint8Array(payload);
  }
}

const createWebAdapter = (): INativePlatform => ({
  platform: 'web',
  keychain: new FallbackWebKeychain(),
  biometrics: new FallbackWebBiometrics(),
  hardware: new FallbackWebHardware(),
});

function resolveNativePlatform(): INativePlatform {
  if (typeof window !== 'undefined' && (window as any).meshApi) {
    const api = (window as any).meshApi;
    return {
      platform: 'electron',
      keychain: {
        getSecret: async (key: string) => {
          const res = await api.auth?.getRefreshTokenRef(key);
          return res ? new TextEncoder().encode(res) : null;
        },
        setSecret: async (key: string, val: Uint8Array) => {
          const str = new TextDecoder().decode(val);
          await api.auth?.storeRefreshTokenRef(key, str);
        },
        deleteSecret: async (key: string) => {
          await api.auth?.deleteRefreshTokenRef(key);
        },
      },
      biometrics: {
        isAvailable: async () => true,
        authenticate: async (reason: string) => {
          if (api.biometric?.verify) {
            return await api.biometric.verify(reason);
          }
          return true;
        },
      },
      hardware: {
        getDeviceId: async () => 'electron_host_node',
        bleTransmit: async (p) => p,
      },
    };
  }

  if (typeof window !== 'undefined' && (window as any).CapacitorBridge) {
    const cap = (window as any).CapacitorBridge;
    return {
      platform: 'capacitor',
      keychain: cap.keychain || new FallbackWebKeychain(),
      biometrics: cap.biometrics || new FallbackWebBiometrics(),
      hardware: cap.hardware || new FallbackWebHardware(),
    };
  }

  return createWebAdapter();
}

export const NativeAdapter: INativePlatform = resolveNativePlatform();
